import React, { useState, useEffect } from 'react';
import { BookOpen, Download, FileText, RefreshCw, Pencil, Check, X } from 'lucide-react';
import api from '@/api/api';
import Pagination from '@/components/Pagination';
import { exportTableToPDF } from '@/utils/pdfExport';
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/dateTimeUtils';

const AccessionRegister = () => {
  const [copies, setCopies] = useState([]);
  const [editingCopyId, setEditingCopyId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [booksWithoutCopies, setBooksWithoutCopies] = useState(0);
  const [collegeInfo, setCollegeInfo] = useState(null);

  useEffect(() => {
    fetchData();
    api.get('/tenants/my-college/settings').then(r => setCollegeInfo(r.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accRes, catRes] = await Promise.all([
        api.get('/library/accession-register'),
        api.get('/library/categories')
      ]);
      const data = accRes.data || {};
      setCopies(data.copies || []);
      setBooksWithoutCopies(data.books_without_copies || 0);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (error) {
      console.error('Error fetching accession register:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAccessionNumbers = async () => {
    if (!window.confirm('This will generate accession numbers for all book copies that don\'t have one. Continue?')) return;
    setGenerating(true);
    try {
      const response = await api.post('/library/generate-accession-numbers');
      alert(response.data?.message || 'Accession numbers generated successfully');
      fetchData();
    } catch (error) {
      alert('Failed to generate accession numbers');
    } finally {
      setGenerating(false);
    }
  };

  const filteredCopies = copies.filter(copy => {
    const matchesSearch = !searchTerm ||
      copy.accession_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      copy.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      copy.author_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      copy.isbn?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || copy.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filteredCopies.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedCopies = filteredCopies.slice(startIdx, startIdx + itemsPerPage);

  const getExportData = () => filteredCopies.map((c, i) => ({
    'Sl No': i + 1,
    'Accession No': c.accession_number || '-',
    'Title': c.title || '-',
    'Copy #': c.copy_number || 1,
    'Status': c.status || 'Available',
    'Author': c.author_name || '-',
    'Publisher': c.publisher_name || '-',
    'ISBN': c.isbn || '-',
    'Category': c.category_name || '-',
    'Rack': c.rack_number || '-',
    'Price': c.price || 0,
    'Date of Entry': c.created_at ? formatDate(c.created_at) : '-'
  }));

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(getExportData());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Accession Register');
    XLSX.writeFile(wb, `Accession_Register_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPDF = () => {
    const columns = [
      { header: 'Sl', dataKey: 'Sl No' },
      { header: 'Accession No', dataKey: 'Accession No' },
      { header: 'Title', dataKey: 'Title' },
      { header: 'Copy #', dataKey: 'Copy #' },
      { header: 'Status', dataKey: 'Status' },
      { header: 'Author', dataKey: 'Author' },
      { header: 'ISBN', dataKey: 'ISBN' },
      { header: 'Category', dataKey: 'Category' },
      { header: 'Price', dataKey: 'Price' }
    ];
    exportTableToPDF('Accession Register', columns, getExportData(), `Accession_Register_${new Date().toISOString().slice(0, 10)}.pdf`, collegeInfo);
  };

  const getStatusBadge = (status) => {
    const styles = {
      Available: 'bg-green-100 text-green-700',
      Issued: 'bg-yellow-100 text-yellow-700',
      Damaged: 'bg-red-100 text-red-700',
      Lost: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accession-register">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accession Register</h1>
          <p className="text-sm text-gray-500 mt-1">
            Complete register of all physical copies ({filteredCopies.length} entries)
          </p>
        </div>
        <div className="flex gap-2">
          {booksWithoutCopies > 0 && (
            <button
              onClick={handleGenerateAccessionNumbers}
              disabled={generating}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50"
              data-testid="generate-accession-btn"
            >
              <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
              Generate ({booksWithoutCopies} books)
            </button>
          )}
          <button onClick={handleExportExcel} disabled={filteredCopies.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
            data-testid="accession-export-excel">
            <Download className="h-4 w-4" /> Excel
          </button>
          <button onClick={handleExportPDF} disabled={filteredCopies.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
            data-testid="accession-export-pdf">
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <input type="text" placeholder="Search by accession no, title, author, ISBN..."
          value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          data-testid="accession-search" />
        <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg">
          <option value="">All Categories</option>
          {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
        </select>
        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg">
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sl</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accession No</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Copy #</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publisher</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">ISBN</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rack</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginatedCopies.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  No accession entries found
                </td>
              </tr>
            ) : (
              paginatedCopies.map((copy, idx) => (
                <tr key={copy.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm text-gray-500">{startIdx + idx + 1}</td>
                  <td className="px-3 py-2">
                    {editingCopyId === copy.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-32 px-2 py-1 border border-blue-300 rounded text-xs font-mono focus:ring-1 focus:ring-blue-500"
                          autoFocus
                          data-testid={`accession-edit-input-${copy.id}`}
                        />
                        <button
                          disabled={savingEdit}
                          onClick={async () => {
                            if (!editValue.trim()) { alert('Accession number is required'); return; }
                            setSavingEdit(true);
                            try {
                              await api.put(`/library/copies/${copy.id}/accession`, { accession_number: editValue.trim() });
                              setEditingCopyId(null);
                              fetchData();
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to update accession number');
                            } finally {
                              setSavingEdit(false);
                            }
                          }}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          data-testid={`accession-save-btn-${copy.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setEditingCopyId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 group">
                        <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">
                          {copy.accession_number || '-'}
                        </span>
                        <button
                          onClick={() => { setEditingCopyId(copy.id); setEditValue(copy.accession_number || ''); }}
                          className="p-1 text-gray-300 hover:text-blue-600 group-hover:text-gray-500"
                          title="Edit accession number"
                          data-testid={`accession-edit-btn-${copy.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-900">{copy.title}</td>
                  <td className="px-3 py-2 text-sm text-center text-gray-600">{copy.copy_number}</td>
                  <td className="px-3 py-2 text-center">{getStatusBadge(copy.status)}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{copy.author_name || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{copy.publisher_name || '-'}</td>
                  <td className="px-3 py-2 text-sm font-mono text-xs text-gray-500">{copy.isbn || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{copy.category_name || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{copy.rack_number || '-'}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-700">
                    {copy.price ? `₹${copy.price.toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {copy.created_at ? formatDate(copy.created_at) : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {startIdx + 1}-{Math.min(startIdx + itemsPerPage, filteredCopies.length)} of {filteredCopies.length}
          </p>
          <div className="flex gap-1">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">Previous</button>
            <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessionRegister;
